import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { adminLoyalty, LoyaltySettings } from '../../api/admin';

// ─── Styled Components ────────────────────────────────────────────────────────
// Mirrors AdminSettings.tsx's styling/structure (this page's explicit template).

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  margin-bottom: 28px;
  font-family: Georgia, serif;
`;

const Section = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 28px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1C1511;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #E8DDD0;
  font-family: Georgia, serif;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: #6B5E4E;
  margin-bottom: 20px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #1C1511;
  min-width: 220px;
`;

const NumberInput = styled.input`
  width: 120px;
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-top: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ApplyButton = styled(SaveButton)`
  background: #1C1511;
  color: #FAF8F3;

  &:hover {
    box-shadow: 0 4px 12px rgba(28, 21, 17, 0.3);
  }
`;

const Toast = styled.div<{ variant: 'success' | 'error' }>`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  margin-top: 16px;
  background: ${props => props.variant === 'success' ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.variant === 'success' ? '#065f46' : '#991b1b'};
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: #6B5E4E;
  text-align: center;
  padding: 24px 0;
`;

const WarningText = styled.p`
  font-size: 12px;
  color: #991b1b;
  margin: -12px 0 16px 236px;
`;

// ─── Global Loyalty Settings Section ──────────────────────────────────────────

const RATE_MAX = 1_000_000;
const EXPIRY_DAYS_MAX = 3650; // 10 years

const GlobalSettingsSection: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pointsPerPound, setPointsPerPound] = useState<number>(0);
  const [pointsToCurrencyRate, setPointsToCurrencyRate] = useState<number>(0);
  const [expiryDays, setExpiryDays] = useState<number>(1);
  const [toast, setToast] = useState<{ variant: 'success' | 'error'; message: string } | null>(null);
  const [fieldWarning, setFieldWarning] = useState<{ field: 'pointsPerPound' | 'pointsToCurrencyRate' | 'expiryDays'; message: string } | null>(null);

  const { data: settingsData, isLoading, isError, refetch } = useQuery<LoyaltySettings>({
    queryKey: ['adminLoyaltySettings'],
    queryFn: adminLoyalty.getSettings,
  });

  useEffect(() => {
    if (settingsData) {
      setPointsPerPound(settingsData.points_per_pound);
      setPointsToCurrencyRate(settingsData.points_to_currency_rate);
      setExpiryDays(settingsData.expiry_days);
    }
  }, [settingsData]);

  const saveMutation = useMutation({
    mutationFn: (payload: LoyaltySettings) => adminLoyalty.updateSettings(payload),
    onSuccess: (data) => {
      setPointsPerPound(data.points_per_pound);
      setPointsToCurrencyRate(data.points_to_currency_rate);
      setExpiryDays(data.expiry_days);
      queryClient.invalidateQueries({ queryKey: ['adminLoyaltySettings'] });
      setToast({ variant: 'success', message: t('admin.loyaltyPoints.saveSuccess') });
      setTimeout(() => setToast(null), 3000);
    },
    onError: () => {
      setToast({ variant: 'error', message: t('admin.loyaltyPoints.saveError') });
      setTimeout(() => setToast(null), 4000);
    },
  });

  const handleSave = () => {
    saveMutation.mutate({
      points_per_pound: pointsPerPound,
      points_to_currency_rate: pointsToCurrencyRate,
      expiry_days: expiryDays,
    });
  };

  if (isLoading) {
    return <LoadingText>{t('admin.loyaltyPoints.loadingSettings')}</LoadingText>;
  }

  if (isError) {
    return (
      <>
        <Toast variant="error">{t('admin.loyaltyPoints.loadError')}</Toast>
        <SaveButton onClick={() => refetch()}>{t('admin.loyaltyPoints.tryAgainButton')}</SaveButton>
      </>
    );
  }

  return (
    <>
      <FieldRow>
        <FieldLabel htmlFor="pointsPerPound">{t('admin.loyaltyPoints.pointsPerPoundLabel')}</FieldLabel>
        <NumberInput
          id="pointsPerPound"
          type="number"
          min={0}
          max={RATE_MAX}
          step="any"
          value={pointsPerPound}
          onChange={e => {
            const raw = parseFloat(e.target.value);
            if (!Number.isFinite(raw) || raw > RATE_MAX) {
              setFieldWarning({ field: 'pointsPerPound', message: t('admin.loyaltyPoints.valueCappedWarning', { max: RATE_MAX.toLocaleString() }) });
            } else if (fieldWarning?.field === 'pointsPerPound') {
              setFieldWarning(null);
            }
            setPointsPerPound(Math.min(RATE_MAX, Math.max(0, raw || 0)));
          }}
        />
      </FieldRow>
      {fieldWarning?.field === 'pointsPerPound' && <WarningText>{fieldWarning.message}</WarningText>}
      <FieldRow>
        <FieldLabel htmlFor="pointsToCurrencyRate">{t('admin.loyaltyPoints.redemptionRateLabel')}</FieldLabel>
        <NumberInput
          id="pointsToCurrencyRate"
          type="number"
          min={0}
          max={RATE_MAX}
          step="any"
          value={pointsToCurrencyRate}
          onChange={e => {
            const raw = parseFloat(e.target.value);
            if (!Number.isFinite(raw) || raw > RATE_MAX) {
              setFieldWarning({ field: 'pointsToCurrencyRate', message: t('admin.loyaltyPoints.valueCappedWarning', { max: RATE_MAX.toLocaleString() }) });
            } else if (fieldWarning?.field === 'pointsToCurrencyRate') {
              setFieldWarning(null);
            }
            setPointsToCurrencyRate(Math.min(RATE_MAX, Math.max(0, raw || 0)));
          }}
        />
      </FieldRow>
      {fieldWarning?.field === 'pointsToCurrencyRate' && <WarningText>{fieldWarning.message}</WarningText>}
      <FieldRow>
        <FieldLabel htmlFor="expiryDays">{t('admin.loyaltyPoints.expiryDaysLabel')}</FieldLabel>
        <NumberInput
          id="expiryDays"
          type="number"
          min={1}
          max={EXPIRY_DAYS_MAX}
          value={expiryDays}
          onChange={e => {
            const raw = parseInt(e.target.value, 10);
            if (!Number.isFinite(raw) || raw > EXPIRY_DAYS_MAX) {
              setFieldWarning({ field: 'expiryDays', message: t('admin.loyaltyPoints.valueCappedWarning', { max: EXPIRY_DAYS_MAX.toLocaleString() }) });
            } else if (fieldWarning?.field === 'expiryDays') {
              setFieldWarning(null);
            }
            setExpiryDays(Math.min(EXPIRY_DAYS_MAX, Math.max(1, raw || 1)));
          }}
        />
      </FieldRow>
      {fieldWarning?.field === 'expiryDays' && <WarningText>{fieldWarning.message}</WarningText>}
      <SaveButton onClick={handleSave} disabled={saveMutation.isPending}>
        {saveMutation.isPending ? t('admin.loyaltyPoints.savingButton') : t('admin.loyaltyPoints.saveButton')}
      </SaveButton>
      {toast && (
        <Toast variant={toast.variant}>{toast.message}</Toast>
      )}
    </>
  );
};

// ─── Apply Rate To All Products Section ───────────────────────────────────────

const ApplyRateSection: React.FC = () => {
  const { t } = useTranslation();
  const [toast, setToast] = useState<{ variant: 'success' | 'error'; message: string } | null>(null);

  const applyMutation = useMutation({
    mutationFn: adminLoyalty.applyRateToAllProducts,
    onSuccess: (data) => {
      const countMessage = data.updated_count === 1
        ? t('admin.loyaltyPoints.applySuccessCountSingular', { count: data.updated_count })
        : t('admin.loyaltyPoints.applySuccessCountPlural', { count: data.updated_count });
      setToast({
        variant: 'success',
        message: `${t('admin.loyaltyPoints.applySuccessPrefix')} ${countMessage}`,
      });
      setTimeout(() => setToast(null), 5000);
    },
    onError: () => {
      setToast({ variant: 'error', message: t('admin.loyaltyPoints.applyError') });
      setTimeout(() => setToast(null), 4000);
    },
  });

  const handleApplyToAll = () => {
    if (window.confirm(t('admin.loyaltyPoints.confirmApplyToAll'))) {
      applyMutation.mutate();
    }
  };

  return (
    <>
      <SectionDescription>
        {t('admin.loyaltyPoints.applyDescription')}
      </SectionDescription>
      <ApplyButton onClick={handleApplyToAll} disabled={applyMutation.isPending}>
        {applyMutation.isPending ? t('admin.loyaltyPoints.applyingButton') : t('admin.loyaltyPoints.applyButton')}
      </ApplyButton>
      {toast && (
        <Toast variant={toast.variant}>{toast.message}</Toast>
      )}
    </>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const AdminLoyaltyPoints: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Container>
      <PageTitle>{t('admin.loyaltyPoints.pageTitle')}</PageTitle>

      <Section>
        <SectionTitle>{t('admin.loyaltyPoints.globalSettingsTitle')}</SectionTitle>
        <GlobalSettingsSection />
      </Section>

      <Section>
        <SectionTitle>{t('admin.loyaltyPoints.applyRateSectionTitle')}</SectionTitle>
        <ApplyRateSection />
      </Section>
    </Container>
  );
};

export default AdminLoyaltyPoints;
